/**
 * Brand-color normalization helpers.
 *
 * Vuetify 3's `<v-color-picker mode="hex">` emits `#RRGGBBAA` (8-char
 * with alpha) into its v-model when the user clicks a swatch or drags
 * the canvas. The backend's validator
 * (`api/src/endpoints/superadmin_distributors.py::_BRAND_COLOR_RE`)
 * accepts only the 6-char form `#RRGGBB`. Without normalization, every
 * swatch click would produce a save attempt that the client validator
 * disables or the server 400s (DistributorEdit, StoreBranding).
 *
 * Bar-raiser P1 on PR-F.
 *
 * Approach: pure functions that accept whatever the picker emits and
 * return either a clean 6-char hex or the original string if it's
 * already short. Unit-tested directly so the Vuetify-mount-test gap
 * (callers' specs stub `<v-color-picker>` to make tests tractable)
 * does not hide future regressions.
 *
 * Consumers as of 2026-08-24: the superadmin DistributorEdit page and
 * the store-editor StoreBranding page. The distributor Defaults page's
 * brand-color card (DefaultsBranding.vue) was removed that day; this
 * module stays because those two callers still use it.
 */

const HEX6_RE = /^#[0-9a-fA-F]{6}$/;
const HEX8_RE = /^(#[0-9a-fA-F]{6})[0-9a-fA-F]{2}$/;

/**
 * Normalize a hex color string emitted by `<v-color-picker>` into the
 * 6-char `#RRGGBB` form the backend accepts.
 *
 * - `null` / `undefined` / `""` -> returned unchanged (caller decides
 *   how to surface "not set").
 * - `#RRGGBB` -> returned unchanged.
 * - `#RRGGBBAA` -> alpha stripped; returns `#RRGGBB`.
 * - Anything else -> returned unchanged so the existing per-page
 *   validator surfaces the right error message. The normalizer is
 *   permissive on input; it is not the validator.
 *
 * @param {*} value the value emitted by `<v-color-picker>` or typed
 *   into the hex text input.
 * @returns {*} the normalized 6-char hex, or the input unchanged when
 *   it does not match the 8-char shape.
 */
export function normalizeHex(value) {
    if (typeof value !== "string") return value;
    const m = value.match(HEX8_RE);
    if (m) return m[1];
    return value;
}

/**
 * Strict validator. Returns true iff the string is exactly `#RRGGBB`.
 * Use this for "should the Save button be enabled?" decisions, NOT for
 * the backend contract (the server has its own validator that runs
 * regardless).
 */
export function isValidBrandHex(value) {
    if (typeof value !== "string") return false;
    return HEX6_RE.test(value);
}
